import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { hotArticles, dailyContestArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const allArticles = [...hotArticles, ...dailyContestArticles];
  const article = allArticles.find((a) => a.id === id) || hotArticles[0];
  const relatedArticles = allArticles.filter((a) => a.id !== id).slice(0, 3);
  const [showComments, setShowComments] = useState(false);

  return (
    <>
      <SEO
        title={`${article.title} | 吃瓜网每日大赛`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},吃瓜网每日大赛,吃瓜爆料,网红黑料`}
        canonicalUrl={`http://irilpt.cn/article/${id}`}
        ogType="article"
        schemaType="Article"
        schemaData={{
          imageUrl: article.imageUrl,
          publishDate: article.publishDate,
          author: article.author,
        }}
        breadcrumbs={[
          { name: '首页', url: 'http://irilpt.cn/' },
          { name: article.category, url: `http://irilpt.cn/category/${article.categorySlug}` },
          { name: article.title, url: `http://irilpt.cn/article/${id}` },
        ]}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors">首页</Link>
              <span>/</span>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-foreground-900 transition-colors">
                {article.category}
              </Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Hero */}
        <section className="w-full px-4 md:px-6 py-8 md:py-0">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden mb-8 md:mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] md:aspect-[21/9] object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full px-4 md:px-6 pb-10">
          <div className="max-w-3xl mx-auto">
            {/* Tags & Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 leading-tight mb-3">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.publishDate}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line w-3.5 h-3.5 flex items-center justify-center text-accent-600"></i>
                {article.hotValue.toLocaleString()} 热度
              </span>
            </div>

            {/* Article Body */}
            <div className="prose prose-lg max-w-none">
              <p className="text-base md:text-lg text-foreground-800 leading-relaxed mb-6">
                <strong className="text-foreground-950">据知情人士透露，</strong>{article.excerpt}
              </p>
              <p className="text-base text-foreground-700 leading-relaxed mb-6">
                该事件在网络上迅速发酵，引发了广泛关注和讨论。多位业内人士纷纷发声，从不同角度对此事进行了分析和解读。截至目前，相关话题已在各大社交平台累计获得超过5000万次阅读量，成为当日最受关注的娱乐事件之一。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed mb-6">
                根据多位消息人士的爆料，事件背后还隐藏着更多不为人知的细节。一位不愿透露姓名的业内人士向本网爆料称，该事件的真相远比表面看到的更加复杂，涉及多方利益纠葛和长期的行业内幕。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed mb-6">
                值得注意的是，当事方至今仍未对此事做出正式回应。相关社交媒体账号的评论区已经涌入大量网友留言，舆论呈现两极分化态势。一部分网友表示支持等待真相水落石出，而另一部分网友则认为应该立即给出说法。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed mb-6">
                <strong className="text-foreground-950">吃瓜网每日大赛将持续关注此事的后续进展，</strong>为各位读者带来最新的跟踪报道。欢迎在评论区留下你的看法和猜测，一起讨论这个热门话题。
              </p>
            </div>

            {/* Share buttons */}
            <div className="flex items-center gap-3 mt-10 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                <i className="ri-weibo-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                <i className="ri-wechat-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                <i className="ri-link w-4 h-4 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Comment / Gossip Wall */}
        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-10">
              <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-2">
                网友热议
              </h3>
              <p className="text-sm text-foreground-500 mb-6">真实网友评论 · 不代表本网立场</p>

              {/* sample comments */}
              <div className="flex flex-col gap-5">
                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=Chinese%20young%20person%20profile%20photo%2C%20simple%20warm%20background%2C%20minimalist%20portrait&width=80&height=80&seq=comment-avatar-01&orientation=squarish"
                      alt="网友头像"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-foreground-900">吃瓜路人甲</span>
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-foreground-900 text-background-50 text-xs">
                        <i className="ri-star-fill text-accent-500 w-2.5 h-2.5 flex items-center justify-center"></i>
                        4.6
                      </span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      天呐这个消息太劲爆了！！我之前就隐隐约约有听说一些风声，没想到真的是这样。坐等后续更多爆料！
                    </p>
                    <span className="text-xs text-foreground-500 mt-2 block">2026-07-07</span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=Chinese%20netizen%20profile%20photo%2C%20simple%20warm%20background%2C%20minimalist%20portrait%2C%20casual%20style&width=80&height=80&seq=comment-avatar-02&orientation=squarish"
                      alt="网友头像"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-foreground-900">深夜吃瓜人</span>
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-foreground-900 text-background-50 text-xs">
                        <i className="ri-star-fill text-accent-500 w-2.5 h-2.5 flex items-center justify-center"></i>
                        4.8
                      </span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      吃瓜网永远是最快的！我在其他平台都还没看到这个消息，这里已经出详细报道了。果断收藏，以后就蹲这里吃瓜了！
                    </p>
                    <span className="text-xs text-foreground-500 mt-2 block">2026-07-07</span>
                  </div>
                </div>
              </div>

              {!showComments && (
                <button
                  onClick={() => setShowComments(true)}
                  className="mt-6 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
                >
                  查看更多评论 ↓
                </button>
              )}

              {showComments && (
                <div className="flex flex-col gap-5 mt-5 pt-5 border-t border-background-200/70">
                  <div className="flex gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img
                        src="https://readdy.ai/api/search-image?query=Chinese%20young%20adult%20profile%20photo%2C%20simple%20warm%20background%2C%20minimalist%20portrait&width=80&height=80&seq=comment-avatar-03&orientation=squarish"
                        alt="网友头像"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-foreground-900">八卦研究所</span>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        这条消息如果属实的话，可能会引发整个行业的连锁反应。希望当事人尽快出来说明情况。
                      </p>
                      <span className="text-xs text-foreground-500 mt-2 block">2026-07-06</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img
                        src="https://readdy.ai/api/search-image?query=Chinese%20user%20profile%20photo%2C%20simple%20warm%20background%2C%20minimalist%20portrait%2C%20mysterious&width=80&height=80&seq=comment-avatar-04&orientation=squarish"
                        alt="网友头像"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-foreground-900">匿名爆料者Z</span>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        我有内部消息，事情远不止这么简单。等着吧，接下来几天还会有更大的料爆出来！大家准备好瓜子饮料！
                      </p>
                      <span className="text-xs text-foreground-500 mt-2 block">2026-07-06</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-8">
              相关爆料
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group block"
                >
                  <article className="rounded-xl overflow-hidden border border-background-200/70 bg-background-50 hover:border-primary-300 transition-all duration-300">
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                        {rel.category}
                      </span>
                      <h4 className="text-base font-semibold font-heading text-foreground-900 mt-3 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {rel.title}
                      </h4>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-foreground-500">{rel.publishDate}</span>
                        <span className="text-xs text-accent-600 font-semibold">
                          {rel.hotValue.toLocaleString()}热度
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}